import { useCallback, useEffect, useMemo, useState } from "react";
import { hasRole } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import api, { formatApiError } from "@/lib/api";
import EmployeePicker from "@/components/EmployeePicker";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, XCircle, MapPin, Clock, Filter } from "lucide-react";

const STATUS_PILL = {
    pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
};

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}

const blankForm = () => ({
    employee_id: "",
    start_date: todayISO(),
    end_date: todayISO(),
    location: "",
    reason: "",
});

export default function Offsite() {
    const { user } = useAuth();
    const role = user?.role;
    const isManager = hasRole(user, "admin", "hr", "department_lead");
    const isEmployee = role === "employee";
    const { confirm } = useConfirm();

    const [rows, setRows] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(blankForm());
    const [saving, setSaving] = useState(false);
    const [decisionOpen, setDecisionOpen] = useState(null); // { row, mode: 'approve' | 'reject' }
    const [decisionNote, setDecisionNote] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter !== "all") params.status = statusFilter;
            const { data } = await api.get("/offsite", { params });
            setRows(data || []);
        } catch (e) {
            toast.error(formatApiError(e, "Failed to load offsite requests"));
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);
    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!isManager) return;
        api.get("/employees").then(({ data }) => setEmployees(data || [])).catch(() => setEmployees([]));
    }, [isManager]);

    const openNew = () => {
        setForm({
            ...blankForm(),
            employee_id: isEmployee ? (user?.employee_id || "") : "",
        });
        setDialogOpen(true);
    };

    const save = async () => {
        if (!form.start_date || !form.end_date) { toast.error("Both dates required"); return; }
        if (form.end_date < form.start_date) { toast.error("End date must be on/after start"); return; }
        if (isManager && !form.employee_id) { toast.error("Pick an employee"); return; }
        if (!form.reason.trim()) { toast.error("Reason is required"); return; }
        setSaving(true);
        try {
            await api.post("/offsite", form);
            toast.success("Offsite request filed");
            setDialogOpen(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        } finally {
            setSaving(false);
        }
    };

    const decide = async () => {
        if (!decisionOpen) return;
        try {
            const url = `/offsite/${decisionOpen.row.id}/${decisionOpen.mode}`;
            await api.put(url, { note: decisionNote });
            toast.success(decisionOpen.mode === "approve" ? "Approved" : "Rejected");
            setDecisionOpen(null); setDecisionNote("");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    const cancel = async (r) => {
        const ok = await confirm({
            title: "Cancel this offsite request?",
            description: r.status === "approved"
                ? "This will also remove the OFFSITE marker from the attendance records that were auto-created."
                : "The request will be permanently removed.",
            confirmLabel: "Cancel request",
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/offsite/${r.id}`);
            toast.success("Cancelled");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed");
        }
    };

    const counts = useMemo(() => {
        const c = { total: rows.length, pending: 0, approved: 0, rejected: 0, days_out: 0 };
        rows.forEach((r) => {
            c[r.status] = (c[r.status] || 0) + 1;
            if (r.status === "approved") c.days_out += Number(r.days_count || 0);
        });
        return c;
    }, [rows]);

    return (
        <div className="px-5 sm:px-8 md:px-12 py-8 sm:py-12 md:py-16" data-testid="offsite-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Field & remote work</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Offsite requests
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        File a request when working from a client site, home, or any location outside the office.
                        Approved days are paid, marked <em>OFFSITE</em> on attendance, and appear on your payslip.
                    </p>
                </div>
                <button onClick={openNew} data-testid="offsite-new-btn"
                    className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90">
                    <Plus className="w-4 h-4" /> New request
                </button>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-ink/10 border border-ink/10">
                <KpiCell label="Total" value={counts.total} testid="offsite-kpi-total" />
                <KpiCell label="Pending" value={counts.pending || 0} icon={Clock} testid="offsite-kpi-pending" />
                <KpiCell label="Approved" value={counts.approved || 0} icon={CheckCircle2} testid="offsite-kpi-approved" />
                <KpiCell label="Rejected" value={counts.rejected || 0} icon={XCircle} testid="offsite-kpi-rejected" />
                <KpiCell label="Approved days" value={counts.days_out} accent testid="offsite-kpi-days" />
            </div>

            <div className="mt-8 bg-white border border-ink/10 p-5 flex items-end gap-3" data-testid="offsite-filters">
                <div className="flex items-center gap-2 text-ink/60 text-xs uppercase tracking-[0.15em]">
                    <Filter className="w-4 h-4" /> Filter
                </div>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="offsite-filter-status">
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                </select>
            </div>

            <div className="mt-6 bg-white border border-ink/10 overflow-x-auto" data-testid="offsite-table-wrap">
                <table className="w-full text-sm">
                    <thead className="bg-ink/5 text-ink/60 text-[10px] uppercase tracking-[0.15em]">
                        <tr>
                            {isManager && <th className="text-left p-3">Employee</th>}
                            <th className="text-left p-3">Dates</th>
                            <th className="text-left p-3">Days</th>
                            <th className="text-left p-3">Location</th>
                            <th className="text-left p-3">Reason</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={isManager ? 7 : 6} className="p-6 text-ink/40 text-center">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={isManager ? 7 : 6} className="p-6 text-ink/40 text-center" data-testid="offsite-empty">No offsite requests match this filter.</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.id} className="border-t border-ink/5 hover:bg-bone" data-testid={`offsite-row-${r.id}`}>
                                {isManager && (
                                    <td className="p-3">
                                        <div className="font-medium text-ink">{r.employee_name}</div>
                                        <div className="text-xs text-ink/50">{r.department || "—"}</div>
                                    </td>
                                )}
                                <td className="p-3 whitespace-nowrap">
                                    <div>{r.start_date}</div>
                                    <div className="text-xs text-ink/50">to {r.end_date}</div>
                                </td>
                                <td className="p-3 tabular-nums">{r.days_count}</td>
                                <td className="p-3">
                                    {r.location ? <span className="inline-flex items-center gap-1 text-xs"><MapPin className="w-3 h-3 text-ink/40" /> {r.location}</span> : <span className="text-ink/30">—</span>}
                                </td>
                                <td className="p-3 max-w-[280px]">
                                    <div className="text-xs text-ink/70 line-clamp-2">{r.reason || "—"}</div>
                                    {r.decision_note && <div className="text-xs text-ink/40 mt-1 italic">Note: {r.decision_note}</div>}
                                </td>
                                <td className="p-3">
                                    <span className={`inline-flex items-center px-2 py-0.5 border text-[10px] uppercase tracking-[0.12em] ${STATUS_PILL[r.status] || STATUS_PILL.pending}`}>{r.status}</span>
                                </td>
                                <td className="p-3 text-right whitespace-nowrap">
                                    {isManager && r.status === "pending" && (
                                        <div className="inline-flex items-center gap-2">
                                            <button onClick={() => { setDecisionOpen({ row: r, mode: "approve" }); setDecisionNote(""); }}
                                                className="text-xs px-2 py-1 border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white"
                                                data-testid={`offsite-approve-${r.id}`}>Approve</button>
                                            <button onClick={() => { setDecisionOpen({ row: r, mode: "reject" }); setDecisionNote(""); }}
                                                className="text-xs px-2 py-1 border border-red-600 text-red-700 hover:bg-red-600 hover:text-white"
                                                data-testid={`offsite-reject-${r.id}`}>Reject</button>
                                        </div>
                                    )}
                                    {(r.status === "pending" || (isEmployee && r.employee_id === user?.employee_id) || isManager) && (
                                        <button onClick={() => cancel(r)}
                                            className="ml-2 text-ink/60 hover:text-red-600"
                                            data-testid={`offsite-cancel-${r.id}`}
                                            title="Cancel request">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {dialogOpen && (
                <NewDialog form={form} setForm={setForm} employees={employees} isManager={isManager}
                    saving={saving} onSave={save} onClose={() => !saving && setDialogOpen(false)} />
            )}
            {decisionOpen && (
                <DecisionDialog row={decisionOpen.row} mode={decisionOpen.mode} note={decisionNote}
                    setNote={setDecisionNote} onConfirm={decide} onClose={() => setDecisionOpen(null)} />
            )}
        </div>
    );
}

function KpiCell({ label, value, icon: Icon, accent, testid }) {
    return (
        <div className={`p-5 ${accent ? "bg-klein text-white" : "bg-white"}`} data-testid={testid}>
            <div className="flex items-start justify-between">
                <div className={`overline ${accent ? "!text-white/70" : ""}`}>{label}</div>
                {Icon && <Icon className={`w-4 h-4 ${accent ? "text-white/70" : "text-ink/40"}`} strokeWidth={1.5} />}
            </div>
            <div className="mt-4 font-display font-black text-3xl tracking-tighter tabular-nums">{value}</div>
        </div>
    );
}

function NewDialog({ form, setForm, employees, isManager, saving, onSave, onClose }) {
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="offsite-dialog">
            <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-5 border-b border-ink/10">
                    <div className="overline">Offsite request</div>
                    <h3 className="mt-1 font-display font-bold text-2xl text-ink">Working from outside the office</h3>
                </div>
                <div className="p-6 space-y-4">
                    {isManager && (
                        <label className="flex flex-col gap-1">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-ink/50">Employee *</span>
                            <EmployeePicker employees={employees} value={form.employee_id} onChange={(v) => setForm((f) => ({ ...f, employee_id: v }))} placeholder="— Select —" testid="offsite-form-employee" />
                        </label>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                        <label className="flex flex-col gap-1">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-ink/50">Start date *</span>
                            <input type="date" value={form.start_date} onChange={set("start_date")}
                                className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="offsite-form-start" />
                        </label>
                        <label className="flex flex-col gap-1">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-ink/50">End date *</span>
                            <input type="date" value={form.end_date} onChange={set("end_date")}
                                className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="offsite-form-end" />
                        </label>
                    </div>
                    <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-[0.15em] text-ink/50">Location</span>
                        <input value={form.location} onChange={set("location")} placeholder="e.g. Client site — Ikeja"
                            className="border border-ink/15 px-3 py-2 text-sm bg-white" data-testid="offsite-form-location" />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-[0.15em] text-ink/50">Reason *</span>
                        <textarea rows={3} value={form.reason} onChange={set("reason")}
                            className="border border-ink/15 px-3 py-2 text-sm bg-white resize-none" data-testid="offsite-form-reason" />
                    </label>
                </div>
                <div className="px-6 py-4 border-t border-ink/10 flex justify-end gap-2">
                    <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm border border-ink/15 hover:bg-ink hover:text-white" data-testid="offsite-form-cancel">Cancel</button>
                    <button onClick={onSave} disabled={saving} className="px-4 py-2 text-sm bg-klein text-white hover:bg-klein/90" data-testid="offsite-form-submit">
                        {saving ? "Submitting…" : "Submit request"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DecisionDialog({ row, mode, note, setNote, onConfirm, onClose }) {
    const label = mode === "approve" ? "Approve" : "Reject";
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="offsite-decision-dialog">
            <div className="bg-white w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-5 border-b border-ink/10">
                    <div className="overline">{label} offsite</div>
                    <h3 className="mt-1 font-display font-bold text-xl text-ink">{row.employee_name}</h3>
                    <p className="text-xs text-ink/50 mt-1">{row.start_date} → {row.end_date} · {row.days_count} day(s)</p>
                </div>
                <div className="p-6 space-y-3">
                    {mode === "approve" && (
                        <p className="text-xs text-ink/60">
                            Approving will mark each weekday in the date range as <strong>OFFSITE</strong> on attendance
                            and include them on the payslip.
                        </p>
                    )}
                    <label className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-[0.15em] text-ink/50">Note (optional)</span>
                        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                            className="border border-ink/15 px-3 py-2 text-sm bg-white resize-none" data-testid="offsite-decision-note" />
                    </label>
                </div>
                <div className="px-6 py-4 border-t border-ink/10 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-ink/15">Cancel</button>
                    <button onClick={onConfirm} data-testid="offsite-decision-confirm"
                        className={`px-4 py-2 text-sm text-white ${mode === "approve" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-red-600 hover:bg-red-700"}`}>
                        {label}
                    </button>
                </div>
            </div>
        </div>
    );
}
